import { useEffect, useState } from 'react';
import { useData } from './DataProvider';
import { getActiveForegroundEvent, cleanRect } from './RectangleOverlay';
import { ref, getDownloadURL, uploadBytes } from "firebase/storage";
import { storage } from '../firebase';

/**
 * AutoInspect handles backfilling of click images to Firebase storage.
 * This component renders nothing but processes mouse click events and uploads
 * missing click images to Firebase in the background.
 * Props:
 *   - appInfo: Object containing clickimagesPath for Firebase storage
 */
export default function AutoInspect({ appInfo }) {
    const { allRows, aiVideoRef, mouseClickRowsWithImages } = useData();
    const [mouseClickImages, setMouseClickImages] = useState([]);

    // Backfill click images to Firebase when mouseClickRowsWithImages changes
    useEffect(() => {
        (async () => {
            let aiVideo = aiVideoRef.current;
            if (!aiVideo) {
                console.log("no current in aiVideoRef", aiVideoRef);
                return;
            }

            let backfilledfiles = 0;
            let mcia = [];

            // get images of each screen at click events
            for (let [index, mce] of mouseClickRowsWithImages.entries()) {
                let mOffsetSecs = mce.VideoOffset / 1000.0;

                // if we have this already, don't repeat
                if(!mouseClickImages.find(i => i.index === index)) {

                    // Ensure we're at the right time in the video
                    await new Promise((resolve) => {
                        const onSeeked = () => {
                            aiVideo.removeEventListener("seeked", onSeeked);
                            resolve();
                        };
                        aiVideo.addEventListener("seeked", onSeeked);
                        aiVideo.currentTime = mOffsetSecs;
                    });

                    // get the foreground window
                    let mActiveEvent = getActiveForegroundEvent(allRows, aiVideo.currentTime);
                    if(mActiveEvent) {
                        let mRect = cleanRect(mActiveEvent.WindowBoundingRectangle);
                        let mCanvas = document.createElement('canvas');
                        mCanvas.width = mRect.Width;
                        mCanvas.height = mRect.Height;
                        let mCtx = mCanvas.getContext('2d');
                        mCtx.drawImage(aiVideo, mRect.X, mRect.Y, mRect.Width, mRect.Height, 0, 0, mCanvas.width, mCanvas.height);

                        // Use pre-computed clickImageFileName from enriched event
                        const clickImageFileName = mce.clickImageFileName;
                        
                        // Check if appInfo and clickimagesPath are available
                        if (!appInfo || !appInfo.clickimagesPath) {
                            console.warn('AutoInspect: appInfo.clickimagesPath not available, skipping image processing');
                            continue;
                        }
                        
                        if (!clickImageFileName) {
                            console.warn('AutoInspect: No clickImageFileName available for event, skipping image processing');
                            continue;
                        }
                        
                        const filePath = `${appInfo.clickimagesPath}/${clickImageFileName}.jpg`;
                        const fileRef = ref(storage, filePath);
                        try {
                            // Check if file exists
                            await getDownloadURL(fileRef);
                        } catch (error) {
                            if (error.code === 'storage/object-not-found') {
                                // generate jpg and upload if missing
                                const jpegBlob = await new Promise(resolve => mCanvas.toBlob(resolve, 'image/jpeg', 1.0));
                                await uploadBytes(fileRef, jpegBlob, { contentType: 'image/jpeg' });
                                backfilledfiles++;
                                console.log(`Backfilled clickimage file: ${filePath} size ${jpegBlob ? jpegBlob.size : 'unknown'}`);
                            }
                        }

                        // build new object for tracking
                        let mci = {
                            index : index,
                            event : mce,
                            activeEvent : mActiveEvent,
                        }

                        mcia.push(mci);
                    }
                }
            }

            if(mcia.length > 0) {
                setMouseClickImages(prev => {
                    // Merge new items with existing ones, avoiding duplicates
                    const existingIndices = new Set(prev.map(i => i.index));
                    const newItems = mcia.filter(mci => !existingIndices.has(mci.index));
                    return [...prev, ...newItems];
                });
            }

            if(mouseClickRowsWithImages.length > 0 && backfilledfiles > 0)
                console.log(`backfilled ${backfilledfiles} clickimages`);
        })();
    }, [mouseClickRowsWithImages, allRows, aiVideoRef, appInfo, mouseClickImages]);

    // Component renders nothing, only performs background image backfilling
    return null;
}
